import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../../utility/Utility";
import Swal from "sweetalert2";

export default function AddHeader() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    logo: null,
    logoPath: "",
    email: "",
    phone: "",
    status: "active",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    logo: "",
    email: "",
    phone: "",
  });

  const validateForm = () => {
    const newErrors = {
      logo: "",
      email: "",
      phone: "",
    };
    let isValid = true;

    if (!formData.logo && !formData.logoPath) {
      newErrors.logo = "Logo is required";
      isValid = false;
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!formData.phone) {
      newErrors.phone = "Phone is required";
      isValid = false;
    } else if (!/^[0-9]{10,15}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be 10-15 digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "logo") {
      if (files && files[0]) {
        setFormData({ ...formData, logo: files[0] });
      }
    } else {
      setFormData({ ...formData, [name]: value });
      if (errors[name]) {
        setErrors({ ...errors, [name]: "" });
      }
    }
  };

  const toggleStatus = () => {
    setFormData({
      ...formData,
      status: formData.status === "active" ? "inactive" : "active"
    });
  };

  const uploadLogo = async () => {
    if (!formData.logo) return null;

    const imageFormData = new FormData();
    imageFormData.append("file", formData.logo);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);
      if (response?.success) {
        return response.url || response.imageUrl || response.filePath;
      } else {
        toast.error(response?.message || "Logo upload failed");
        return null;
      }
    } catch (err) {
      toast.error("Network error during logo upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const uploadedLogoPath = await uploadLogo();
      if (!uploadedLogoPath && !formData.logoPath) {
        toast.error("Please upload a logo");
        setLoading(false);
        return;
      }

      const headerData = {
        phone: formData.phone,
        email: formData.email,
        logo: uploadedLogoPath || formData.logoPath,
        status: formData.status
      };

      const response = await postData("api/admin/header-settings/create", headerData);

      if (response.success) {
        Swal.fire({
          title: "Success!",
          text: "Header created successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/header-list");
        });
      } else {
        toast.error(response.message || "Failed to add header");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Header Management</h4>
              <p className="fw-semibold my-2">Add New Header</p>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Header Information</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Logo Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="logo" className="form-label fw-medium">
                          Logo <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="logo"
                          name="logo"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/jpeg, image/png, image/svg+xml"
                        />
                        <small className="text-muted d-block mt-1">
                          Required: Recommended 200x50px, Max 2MB (JPEG/PNG/SVG)
                        </small>
                        {formData.logo && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(formData.logo)}
                              alt="Logo Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "100px" }}
                            />
                          </div>
                        )}
                        {errors.logo && (
                          <div className="invalid-feedback">{errors.logo}</div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="email" className="form-label fw-medium">
                          Email <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          placeholder="Enter contact email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="phone" className="form-label fw-medium">
                          Phone <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                          placeholder="Enter contact phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                        {errors.phone && (
                          <div className="invalid-feedback">{errors.phone}</div>
                        )}
                      </div>
                    </div>

                    {/* Status Switch */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="status"
                            checked={formData.status === "active"}
                            onChange={toggleStatus}
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="status">
                            {formData.status === "active" ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-danger">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-content-save me-1"></i>
                              Save Header
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}